// ContactUs.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Heading,
  useToast,
  Divider,
} from "@chakra-ui/react";
import axios from "axios";
import { API_ENDPOINTS } from "../api/api.js";
import { v4 as uuidv4 } from "uuid";

/* ------------------------------
   Helpers: UTM/session/device/etc.
   ------------------------------ */
const STORAGE_KEYS = {
  SESSION: "vh_session_id",
  PERSIST_UTM: "vh_utms",
};

function readUtmFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "gclid",
      "fbclid",
      "referral_code",
    ].forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v;
    });
    return utm;
  } catch (e) {
    return {};
  }
}

function persistUtms() {
  try {
    const utm = readUtmFromUrl();
    if (Object.keys(utm).length) {
      const existing = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.PERSIST_UTM) || "{}"
      );
      localStorage.setItem(
        STORAGE_KEYS.PERSIST_UTM,
        JSON.stringify({ ...existing, ...utm })
      );
    }
  } catch {}
}

function getPersistedUtms() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEYS.PERSIST_UTM) || "{}");
  } catch {
    return {};
  }
}

function getSessionId() {
  try {
    let s = localStorage.getItem(STORAGE_KEYS.SESSION);
    if (!s) {
      s = uuidv4();
      localStorage.setItem(STORAGE_KEYS.SESSION, s);
    }
    return s;
  } catch {
    return uuidv4();
  }
}

function deviceTypeFromUA(
  ua = (typeof navigator !== "undefined" && navigator.userAgent) || ""
) {
  const uaL = ua.toLowerCase();
  if (/mobile|iphone|ipod|android|blackberry|iemobile|opera mini/.test(uaL))
    return "mobile";
  if (/tablet|ipad|playbook/.test(uaL)) return "tablet";
  return "desktop";
}

async function getGaClientId() {
  try {
    if (
      typeof window !== "undefined" &&
      window.ga &&
      typeof window.ga.getAll === "function"
    ) {
      const trackers = window.ga.getAll();
      if (trackers && trackers.length) {
        const tid = trackers[0].get("clientId");
        if (tid) return tid;
      }
    }
    if (typeof document !== "undefined") {
      const gaCookie = document.cookie
        .split("; ")
        .find((row) => row.startsWith("_ga="));
      if (gaCookie) {
        return decodeURIComponent(gaCookie.split("=")[1]);
      }
    }
  } catch (e) {}
  return null;
}

/* ------------------------------
   Component
   ------------------------------ */
export default function ContactUs() {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    if (typeof window !== "undefined") persistUtms();

    async function collect() {
      try {
        const now = new Date();
        const utms = getPersistedUtms();
        const meta = {
          utms,
          referrer:
            (typeof document !== "undefined" && document.referrer) || null,
          landing_page:
            (typeof window !== "undefined" &&
              window.location.pathname + window.location.search) ||
            null,
          page_url:
            (typeof window !== "undefined" && window.location.href) || null,
          timestamp: now.toISOString(),
          timezone:
            (typeof Intl !== "undefined" &&
              Intl.DateTimeFormat().resolvedOptions().timeZone) ||
            null,
          language:
            (typeof navigator !== "undefined" && navigator.language) || null,
          user_agent:
            (typeof navigator !== "undefined" && navigator.userAgent) || null,
          screen_width:
            (typeof window !== "undefined" && window.screen?.width) || null,
          screen_height:
            (typeof window !== "undefined" && window.screen?.height) || null,
          viewport_width:
            (typeof window !== "undefined" && window.innerWidth) || null,
          viewport_height:
            (typeof window !== "undefined" && window.innerHeight) || null,
          device_type: deviceTypeFromUA(),
          session_id: getSessionId(),
          ga_client_id: await getGaClientId(),
        };
        setMetadata(meta);
      } catch (e) {
        setMetadata({});
      }
    }

    collect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.target);
      const basePayload = {
        name: formData.get("name") || null,
        email: formData.get("email") || null,
        phone: formData.get("phone") || null,
        message: formData.get("message") || null,
      };

      // Ensure metadata collected
      const meta = metadata || {};

      // Build payload that includes BOTH nested metadata (for future use)
      // and flattened top-level fields (for backwards compatibility)
      const payload = {
        ...basePayload,
        metadata: meta,
        // top-level utms (flattened)
        utms: meta.utms || {},
        referrer: meta.referrer || null,
        landing_page: meta.landing_page || null,
        page_url: meta.page_url || null,
        timestamp: meta.timestamp || null,
        timezone: meta.timezone || null,
        language: meta.language || null,
        user_agent: meta.user_agent || null,
        screen_width: meta.screen_width || null,
        screen_height: meta.screen_height || null,
        viewport_width: meta.viewport_width || null,
        viewport_height: meta.viewport_height || null,
        device_type: meta.device_type || null,
        session_id: meta.session_id || null,
        ga_client_id: meta.ga_client_id || null,
      };

      // send JSON
      const res = await axios.post(
        API_ENDPOINTS.pentagonContact.create,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      toast({
        title: "Message sent!",
        description: res.data?.message || "We’ll get back to you soon.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      e.target.reset();
    } catch (err) {
      console.error("Contact submit error:", err);
      toast({
        title: "Oops!",
        description:
          err?.response?.data?.message ||
          "Something went wrong. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg="gray.100" p={6}>
      <Box
        bg="white"
        p={0}
        rounded="2xl"
        shadow="2xl"
        w="100%"
        maxW="650px"
        overflow="hidden"
      >
        <Box
          bgGradient="linear(to-r, #7B1E1E, #1E3C7B)"
          py={6}
          textAlign="center"
        >
          <Heading size="lg" color="white">
            Contact Us
          </Heading>
        </Box>

        <Divider />

        <Box p={8}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6}>
              <FormControl isRequired>
                <FormLabel color="gray.700">Full Name</FormLabel>
                <Input type="text" name="name" placeholder="Enter Full Name" />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.700">Contact Number</FormLabel>
                <Input type="tel" name="phone" placeholder="Your Number" />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color="gray.700">Email</FormLabel>
                <Input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.700">Message</FormLabel>
                <Textarea name="message" placeholder="Type your message..." />
              </FormControl>

              <Button
                type="submit"
                borderRadius="full"
                bgGradient="linear(to-r, #7B1E1E, #1E3C7B)"
                color="white"
                px={10}
                py={6}
                fontWeight="bold"
                isLoading={loading}
              >
                Send Message
              </Button>
            </VStack>
          </form>
        </Box>
      </Box>
    </Flex>
  );
}
